/* eslint-disable */
import * as d3Scale from 'd3-scale';
import * as d3Geo from 'd3-geo';
import * as d3GeoProjection from 'd3-geo-projection';
import * as topojsonClient from 'topojson-client';
import Utils from './utils';
import DataModel from './models/dataModel';
import Region from './models/regionModel';
import MapView from './views/mapView';
import PulloutView from './views/pulloutView';
import LocatorView from './views/locatorView';
import TooltipView from './views/tooltipView';
import OverlayView from './views/overlayView';
import KeyView from './views/keyView';
import PanelView from './views/panelView';
import View from './views/view';

Utils.polyfill();

export default class Map {
    constructor(userConfig) {
        this.defaultConfig = {
            topojson: null, // required; topojson representing the geographical features
            data: false,    // data object
            container: document.body,
            keyViewContainer: false,
            mapViewContainer: false,
            panelViewContainer: false,
            colourScales: false,
            mapTemplates: false,
            panelTemplate: false,
            keyTemplates: false,
            tooltipTemplate: false,
            projection: 'mercator', // default: mercator; must currently be an enum of [albersUsa, mercator], should be expanded
            aspectRatio: 0.75,
            enableFullScreenTest: false,
            deviceCanHover: false,
            pulloutBoxIds: false,
            overlayView: {
                enable: true,
                callToActionText: "Tap to explore",
                closeMapText: "Close Map",
            },
        };
        // @TODO refactor so config options are available at this.config.option rather that this.option
        this.setConfig(userConfig);
        this.container.classList.add('news-vj-map-module');
        this.geography = topojsonClient.feature(this.topojson, this.topojson.objects.boundaries);
        this.mergedGeography = topojsonClient.merge(this.topojson, this.topojson.objects.boundaries.geometries);
        // @TODO implment meshed geography layer on mapView to reduce stroking
        //this.meshedGeography = topojsonClient.mesh(this.topojson, this.topojson.objects.boundaries);
        this.topicHandlersMap = {};
        this.region = this.getModelAllDataAllFeatures();
        this.mergedRegion = new Region(this.mergedGeography, {});
        //this.meshedRegion = new Region(this.meshedGeography, {});

        // Dotted borders
        this.dottedBlack = new Region(topojsonClient.feature(this.topojson, this.topojson.objects.disputedBlack), {});
        this.dottedWhite = new Region(topojsonClient.feature(this.topojson, this.topojson.objects.disputedWhite), {});

        const reducedRegions = this.getReducedRegions(this.pulloutBoxIds);
        if (this.pulloutBoxIds.length > 0) {
            this.regionForPullout = reducedRegions.removed;
            // if single feature, e.g. when Shetland & Orkney combined
            this.singleFeatureBoundingBox = this.getSingleFeatureBoundingBox(this.regionForPullout);
            this.aspectRatioPullout = this.getAspectRatio(this.singleFeatureBoundingBox);
        }

        // @TODO make modelForFocus based on mergedGeography for performant resets!
        this.regionForFocus = reducedRegions.reduced;

        this.views = [];

        const width = 1000;
        const dimensions = [width, width * this.aspectRatio];
        const dimensionsPullout = [width, width * this.aspectRatioPullout];

        this.pulloutProjection = this.createProjection(this.projection, this.regionForPullout, dimensionsPullout);
        this.projection = this.createProjection(this.projection, this.region, dimensions);

        const mapViewContainer = this.mapViewContainer || this.createViewContainer('map', true);
        const panelViewContainer = this.panelViewContainer || mapViewContainer;
        this.mapView = new MapView(
            this, mapViewContainer,
            {
                model: this.region,
                modelForFocus: this.regionForFocus,
                projection: this.projection,
                dimensions: dimensions,
                templates: this.mapTemplates,
                enableFullScreenTest: this.enableFullScreenTest,
                canHover: this.deviceCanHover,
                hasOverlay: this.overlayView.enable,
                dottedBlack: this.dottedBlack,
                dottedWhite: this.dottedWhite,
            }
        );

        const initialViews = [
            this.mapView,
            new LocatorView(
                this, mapViewContainer,
                {
                    model: this.mergedGeography,
                    projection: this.projection,
                    dimensions: dimensions,
                    aspectRatio: this.aspectRatio,
                }
            ),
            new TooltipView(
                this, mapViewContainer,
                {
                    model: this.region,
                    template: this.tooltipTemplate,
                    canHover: this.deviceCanHover,
                }
            ),
            new PanelView(
                this, panelViewContainer,
                {
                    templates: this.panelTemplate,
                    data: this.data,
                }
            ),
        ];

        if (this.keyViewContainer) {
            this.attachView(new KeyView(this, this.keyViewContainer, { templates: this.keyTemplates }));
        }

        initialViews.forEach((view) => {
            this.attachView(view);
        });

        // instantiate and attach the pulloutView if the end user has specified a region to pullout
        if (this.pulloutBoxIds) {
            this.attachView(new PulloutView(
                this, mapViewContainer,
                {
                    modelForPullout: this.regionForPullout,
                    projection: this.pulloutProjection,
                    dimensions: dimensionsPullout,
                    aspectRatio: this.aspectRatioPullout,
                }
            ));
        }

        if (!this.deviceCanHover && this.overlayView.enable === true) {
            this.attachView(new OverlayView(this, mapViewContainer,
                {
                    model: this.region,
                    ctaText: this.overlayView.callToActionText,
                    closeText:  this.overlayView.closeMapText,
                }
            ));
        }

        this.setupListeners();
    }

    setupListeners() {
        window.addEventListener('resize', Utils.debounce(
            this.updateContainerDimensions.bind(this), 250, false
        ));
    }

    currentScale() {
        return this.mapView.getTransform();
    }

    zoomBy(factor, duration = 300) {
        this.mapView.zoomBy(factor, duration);
    }

    zoomTo(scale, duration = 300) {
        this.mapView.zoomTo(scale, duration);
    }

    getContainerDimensions() {
        const bounds = this.container.getBoundingClientRect();
        return [bounds.bottom - bounds.top, bounds.right - bounds.left];
    }

    updateContainerDimensions() {
        const currentDimensions = this.getContainerDimensions();
        if (!this.containerDimensions || currentDimensions[0] !== this.containerDimensions[0] || currentDimensions[1] !== this.containerDimensions[1]) {
            this.notify('container-resize', {
                dimensions: currentDimensions,
            });
            this.containerDimensions = currentDimensions;
        }
    }

    updateGeography(featureCollection) {
        this.region.setTopology(featureCollection);
    }

    updateData(data) {
        DataModel.updateData(data);
    }

    renderData(key, callback) {
        const keyOfInterest = key;
        const colourScale = this.getColourScale(this.colourScales[keyOfInterest]);

        this.views.forEach((view) => {
            if (view.renderData) {
                view.renderData(keyOfInterest, colourScale);
            }
        });

        if (callback) {
            callback.call();
        }
    }

    attachView(view) {
        this.views.push(view);
    }

    subsetData(oldDataset, subsetKeys) {
        const newDataset = {};
        const subsetKeysLength = subsetKeys.length;

        for (let i = 0; i < subsetKeysLength; i++) {
            const key = subsetKeys[i];
            newDataset[key] = oldDataset[key];
        }

        return newDataset;
    }

    // model representing the entire map
    getModelAllDataAllFeatures() {
        return new Region(this.geography, DataModel.getData());
    }

    /**
     * model representing the visible 'main' map (the whole map, minus the pullout)
     * returns:
     * @reducedRegion - the original region minus the removed features
     * @removedRegion - the removed features only
     */
    getReducedRegions(removedGssids) {
        removedGssids = removedGssids || [];
        const reducedGeography = {
            type: "FeatureCollection",
            features: this.geography.features.slice(),
        };
        const removedGeography = {
            type: "FeatureCollection",
            features: [],
        };

        for (let i = 0; i < reducedGeography.features.length; i++) {
            if (removedGssids.indexOf(reducedGeography.features[i].properties.id) > -1) {
                const removedFeature = reducedGeography.features.splice(i, 1)[0];
                removedGeography.features.push(removedFeature);
            }
        }

        const reducedRegion = new Region(reducedGeography, DataModel.getData());
        const removedRegion = new Region(removedGeography, DataModel.getData());
        const reducedRegions = {
            reduced: reducedRegion,
            removed: removedRegion,
        };

        return reducedRegions;
    }

    getColourScale(colourConfig) {
        const config = colourConfig || {};
        let domain = config.thresholds || [];
        let range = config.colours || [];
        let scale = d3Scale.scaleThreshold;

        if (domain.length === range.length) {
            scale = d3Scale.scaleOrdinal;

            if (domain.length === 0 || range.length === 0) {
                return d3Scale.scaleOrdinal(d3Scale.schemeCategory20);
            }

            range = range.length !== 0 ? range : ['red', 'green', 'blue'];
            return d3Scale.scaleOrdinal()
                .domain(domain)
                .range(range)
                .unknown('transparent');
        }

        domain = domain.length !== 0 ? domain : [20, 40, 60, 80];
        range = range.length !== 0 ? range : ['#000000', '#404040', '#808080', '#c0c0c0', '#ffffff'].reverse();
        return scale.call()
            .domain(domain)
            .range(range);
    }

    render() {
        this.views.forEach((view) => {
            if (view.render) {
                view.render(this.region);
            }
        });
    }

    setConfig(config) {
        if (!config) {
            throw new Error('Missing config data. Please refer to documentation.');
        }

        for (const key in this.defaultConfig) {
            if (config.hasOwnProperty(key)) {
                this.setConfigOption(key, config[key]);
            }
            else {
                this.setConfigOption(key, this.defaultConfig[key]);
            }
        }
    }

    setConfigOption(option, value) {
        if (value === null || typeof value === 'undefined') {
            throw new Error(`Config option '${option}' was set to an invalid value.`);
        }
        else {
            this[option] = value;
        }
        if (option === 'data') {
            DataModel.updateData(value);
        }
    }

    createViewContainer(className, ariaHidden = false) {
        const container = this.container || document.body;
        const newElement = document.createElement('div');

        newElement.className = className;
        if (ariaHidden) {
            newElement.setAttribute('aria-hidden', true);
        }
        container.appendChild(newElement);

        return newElement;
    }

    enterFullScreen(data) {
        this.notify('enter-full-screen', data);
    }

		// @todo check animation duration is a valid value
    reset(animationDuration) {
        let duration = 0;
        if (animationDuration) {
            duration = animationDuration;
        }

        this.notify('reset-map', {
            animationDuration: duration,
        });
    }

    notify(topic, data) {
        const handlers = this.topicHandlersMap[topic] || [];
        for (let i = 0; i < handlers.length; i++) {
            const handler = handlers[i];
            handler(data);
        }
    }

    addTopicHandler(topic, callback) {
        if (!Array.isArray(this.topicHandlersMap[topic])) {
            this.topicHandlersMap[topic] = [];
        }

        this.topicHandlersMap[topic].push(callback);
    }

    createProjection(userProjection, model, extent) {
        const projectionType = typeof userProjection;
        let projection = null;

        if (projectionType === 'function') {
            projection = userProjection;
        }
        else if (projectionType === 'string') {
            if (userProjection === 'albersUsa') {
                projection = d3Geo.geoAlbersUsa().fitSize(extent, model);
            }
            else if (userProjection === 'mercator') {
                projection = d3Geo.geoMercator().fitSize(extent, model);
            }
            else if (userProjection === 'robinson') {
                projection = d3GeoProjection.geoRobinson().fitSize(extent, model);
            }
            else if (userProjection === 'identity') {
                projection = d3Geo.geoIdentity().fitSize(extent, model);
            }
            else {
                throw new Error('Invalid projection string: ', userProjection);
            }
        }
        else {
            throw new Error('Invalid projection type: ', projectionType);
        }

        return projection;
    }

    focusFeatureById(featureId, viewportFillFactor, animationDuration) {
        const feature = this.region.getFeatureById(featureId);
        const fillFactor = viewportFillFactor || null;
        const duration = animationDuration || null;
        const mobileDeviceButNotFullScreen = (!this.deviceCanHover && document.querySelectorAll('.map--full-screen').length === 0);
        if (feature) {
            if (mobileDeviceButNotFullScreen) {
                this.enterFullScreen();
                setTimeout(() => {
                    this.notify('focus-feature', {
                        feature: feature,
                        viewportFillFactor: fillFactor,
                        animationLength: duration,
                    });
                }, 300);
            }
            else {
                this.notify('focus-feature', {
                    feature: feature,
                    viewportFillFactor: fillFactor,
                    animationLength: duration,
                });
            }
        }
    }

    getSingleFeatureBoundingBox (model) {
      return d3Geo.geoPath().bounds(model.features[0]);
    }

    getAspectRatio (bbox) {
      const width = bbox[1][0] - bbox[0][0];
      const height = bbox[1][1] - bbox[0][1];
      return width / height;
    }
}
